import { ArrowDownWideNarrow } from "lucide-react";
import { DateRangePicker } from "../DateRangePicker";
import { LessonDropdown } from "../LessonDropdown";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import type { Lesson } from "./types";
import { CHAT_HISTORY_ORDER_LABELS, isChatHistoryOrder, type ChatHistoryOrder } from "@/lib/session-time";

interface SessionFiltersProps {
  startDate: string;
  endDate: string;
  selectedLesson: Lesson | null;
  userId: string;
  hasActiveFilters: boolean;
  /** The order asked for. After a fallback the list is in session-start order; ChatPanel's notice says so. */
  orderBy: ChatHistoryOrder;
  /** True while a history page loads; the order cannot change then. */
  isOrderLocked: boolean;
  onOrderByChange: (order: ChatHistoryOrder) => void;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  onLessonChange: (lesson: Lesson | null) => void;
  onApplyFilters: () => void;
  onClearFilters: () => void;
}

export const SessionFilters = ({
  startDate,
  endDate,
  selectedLesson,
  userId,
  hasActiveFilters,
  orderBy,
  isOrderLocked,
  onOrderByChange,
  onStartDateChange,
  onEndDateChange,
  onLessonChange,
  onApplyFilters,
  onClearFilters,
}: SessionFiltersProps) => {
  return (
    <div className="mt-3 flex flex-wrap items-end gap-3 rounded-lg border bg-muted/50 p-3">
      <DateRangePicker
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={onStartDateChange}
        onEndDateChange={onEndDateChange}
        localDays
      />
      <LessonDropdown
        value={selectedLesson}
        onChange={onLessonChange}
        userId={userId}
      />
      <div className="flex items-end gap-2">
        {/* Order: newest first by save time or by session start */}
        <Select
          value={orderBy}
          disabled={isOrderLocked}
          onValueChange={(value) => {
            if (isChatHistoryOrder(value)) onOrderByChange(value);
          }}
        >
          <SelectTrigger size="sm" className="h-8 gap-1.5 text-xs" aria-label="Order sessions by">
            <ArrowDownWideNarrow className="h-4 w-4" aria-hidden="true" />
            <SelectValue>
              {CHAT_HISTORY_ORDER_LABELS.find((option) => option.value === orderBy)?.label}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {CHAT_HISTORY_ORDER_LABELS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button onClick={onApplyFilters} size="sm" className="h-8">
          Apply
        </Button>
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8"
            onClick={onClearFilters}
          >
            Clear
          </Button>
        )}
      </div>
    </div>
  );
};
